import React from "react";
import {
  Control,
  Controller,
  FieldValues,
  Path,
  UseFormSetValue,
} from "react-hook-form";
import {
  Dropdown,
  makeStyles,
  Option,
  shorthands,
} from "@fluentui/react-components";
import { Dismiss20Regular } from "@fluentui/react-icons";
import { IDropdownOption } from "../../interfaces/IDropdownOptions";
import { Field } from "./Field";
import { t } from "../../i18n/i18n";

const useStyles = makeStyles({
  dismissIcon: {
    ...shorthands.padding(`9px 0`),
    marginRight: "4px",
  },
});

type ControlledDropdownProps<T extends FieldValues> = {
  name: keyof T;
  control: Control<T, any>;
  label: string | JSX.Element;
  customLabel?: JSX.Element;
  options: IDropdownOption[];
  disabled?: boolean;
  required?: boolean;
};
export const ControlledDropDown = <T extends {}>({
  name,
  control,
  label,
  options,
  disabled,
  required,
}: ControlledDropdownProps<T>) => {
  return (
    <Controller
      name={name as any}
      control={control}
      rules={{ required: required ? t("RequiredField") : false }}
      render={({
        field: { value, onChange },
        fieldState: { error },
        formState: { disabled: formDisabled },
      }) => (
        <Field
          label={label}
          required={required ?? false}
          validationMessage={error ? error.message : undefined}
          validationState={error ? "error" : undefined}
        >
          <Dropdown
            value={
              value || value === 0
                ? options.find((x) => x.key === value)?.text
                : ""
            }
            onOptionSelect={(e, d) => {
              onChange(Number(d.optionValue));
            }}
            selectedOptions={[value || value === 0 ? String(value) : ""]}
            disabled={(disabled || formDisabled) ?? false}
            style={{ minWidth: "auto" }}
          >
            {options.map((x) => (
              <Option
                key={x.key}
                value={x.key.toString()}
                disabled={x.disabled ? x.disabled : false}
              >
                {x.text}
              </Option>
            ))}
          </Dropdown>
        </Field>
      )}
    />
  );
};

type ControlledDropdownClearableProps<T extends FieldValues> = {
  name: Path<T>;
  control: Control<T, any>;
  items: any[];
  label: string | JSX.Element;
  customLabel?: JSX.Element;
  disabled?: boolean;
  required?: boolean;
  setValue: UseFormSetValue<any>;
  setValueString: string;
};
export const ControlledDropDownClearable = <T extends {}>({
  name,
  items,
  control,
  label,
  disabled,
  required,
  setValue,
  setValueString,
}: ControlledDropdownClearableProps<T>) => {
  const classes = useStyles();
  return (
    <Controller
      name={name}
      control={control}
      render={({ field: { value, onChange } }) => {
        const typedValue = value as { id: string; name: string } | null;
        const selectedId = typedValue?.id ?? null;
        const selectedName = typedValue?.name ?? "";
        return (
          <Field label={label} required={required ?? false}>
            <Dropdown
              clearable
              clearButton={<Dismiss20Regular className={classes.dismissIcon} />}
              disabled={disabled}
              selectedOptions={selectedId ? [selectedId] : []}
              value={selectedId ? selectedName : ""}
              onOptionSelect={(_, data) => {
                const picked =
                  items.find((i) => i.id === data.optionValue) ?? null;
                onChange(picked);
                setValue(setValueString, picked?.id ?? null);
              }}
            >
              {items.map((p) => (
                <Option key={p.id} value={p.id}>
                  {p.name}
                </Option>
              ))}
            </Dropdown>
          </Field>
        );
      }}
    />
  );
};
